import { FC, useState } from 'react';
import {
  Button,
  Select,
  SelectOption,
  Spacer,
  TextSubtitle,
  TextTitle,
  RadioGroup,
  Radio,
} from '@pypestream/design-system';
import {
  TranslationComponent,
  TranslationKeys,
  useTranslation,
} from '@pypestream/translations';

import { GenericModal, GenericModalProps } from '..';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../../xstate/app.xstate';
import { capitalizeWords } from '../../../utils';

export type PublishModalProps = GenericModalProps & {
  draftVersionId?: string;
};

export const PublishModal: FC<PublishModalProps> = ({
  draftVersionId,
  modalRef,
  onClose: onCloseModal,
}) => {
  const [t] = useTranslation();
  const { envs, botType, bots } = useProStudioCtxSelector((ctx) => ({
    envs: ctx.envs || [],
    botType: ctx.selectedBot?.botType,
    bots: ctx.solutions || [],
  }));

  const isSurvey = botType === 'survey';

  const [selectedEnv, setSelectedEnv] = useState<string>('sandbox');
  const [selectedBots, setSelectedBots] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const onClose = () => {
    onCloseModal?.();
    setSelectedEnv('sandbox');
    setSelectedBots([]);
  };

  const onPublish = () => {
    if (!selectedEnv || !draftVersionId || !botType) {
      return;
    }

    setIsLoading(true);

    sendProStudioEvent({
      type: 'publishBot',
      payload: {
        env: selectedEnv,
        version: draftVersionId,
        botType,
        bots: selectedBots,
        cb: () => {
          onClose();
          setIsLoading(false);
        },
      },
    });
  };

  return (
    <GenericModal
      test-id="ps-publish-version-modal"
      modalRef={modalRef}
      onClose={onClose}
      onError={() => setIsLoading(false)}
      hideCloseBtn={isLoading}
      stayOnClickOutside={isLoading}
      stayOnEsc={isLoading}
    >
      <TextTitle size="small">
        <TranslationComponent i18nKey="pro-studio/navigation:publish_modal.title.part1">
          Publish
        </TranslationComponent>{' '}
        {isSurvey && (
          <>
            <TranslationComponent i18nKey="pro-studio/navigation:publish_modal.title.solution_type">
              Survey
            </TranslationComponent>{' '}
          </>
        )}
        <span color="primary">{draftVersionId}</span>
      </TextTitle>
      <Spacer />
      <TextSubtitle
        test-id="ps-publish-version-modal-select-env-title"
        size="small"
      >
        <TranslationComponent i18nKey="pro-studio/navigation:publish_modal.form.part1.title">
          Select an environment:
        </TranslationComponent>
      </TextSubtitle>
      <Spacer size="small" />
      <RadioGroup
        value={selectedEnv}
        orientation="horizontal"
        size="small"
        test-id="ps-publish-version-modal-select-env"
        onChange={(e) => setSelectedEnv(e.target.value as string)}
      >
        {[...envs].reverse().map((env) => (
          <Radio key={env} value={env} disabled={isLoading}>
            {capitalizeWords(env)}
          </Radio>
        ))}
      </RadioGroup>
      {isSurvey && (
        <>
          <Spacer size="large" />
          <TextSubtitle size="small">
            <TranslationComponent i18nKey="pro-studio/navigation:publish_modal.form.part2.title">
              For the following solution
            </TranslationComponent>
          </TextSubtitle>
          <Spacer size="small" />
          <Select
            test-id="ps-publish-version-modal-select-main-solution"
            multiple
            placeholder={
              t(
                'pro-studio/navigation:publish_modal.form.part2.select.placeholder',
                {
                  defaultValue: 'Select solution',
                }
              ) || ''
            }
            variant="outlined"
            value={selectedBots}
            onChange={(e) => {
              const value = e.target.value as string[];
              setSelectedBots(value);
            }}
          >
            {bots
              .filter(
                ({ botType: botTypeFromBot }) => botTypeFromBot !== 'survey'
              )
              ?.map((bot, index) => {
                const isPublished =
                  !!bot.versions?.length && bot.versions.length > 1;

                return (
                  <SelectOption
                    test-id={`ps-publish-${bot.displayName?.toLowerCase()}-option`}
                    key={`${bot}-${index}`}
                    value={bot.id}
                    // disabled if bot wasn't published yet (has only one draft version)
                    disabled={!isPublished}
                  >
                    {`${bot.displayName} ${isPublished ? '' : '(not yet published)'}`}
                  </SelectOption>
                );
              })}
          </Select>
          <Spacer size="large" />
        </>
      )}
      <Button
        test-id="ps-publish-version-modal-publish-btn"
        size="large"
        slot="footer"
        disabled={!selectedEnv || isLoading}
        onClick={onPublish}
      >
        <TranslationComponent
          i18nKey={
            `pro-studio/navigation:publish_modal.button.${
              isLoading ? 'loading' : 'static'
            }` as TranslationKeys
          }
        >
          {isLoading ? 'Publishing' : 'Publish'}
        </TranslationComponent>
      </Button>
    </GenericModal>
  );
};
